import { useState, useRef, useEffect } from 'react';

interface GMBConnectionStatusProps {
  connectionData: {
    business_name: string | null;
    last_synced_label: string | null;
  } | null;
  onSync: () => void;
  onDisconnect: () => void;
  isSyncing: boolean;
  darkMode: boolean;
}

export default function GMBConnectionStatus({
  connectionData,
  onSync,
  onDisconnect,
  isSyncing,
  darkMode,
}: GMBConnectionStatusProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowDisconnectConfirm(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const businessName = connectionData?.business_name || 'Connected';
  const lastSynced = connectionData?.last_synced_label || 'Never';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          darkMode
            ? 'text-teal-400 hover:bg-gray-700'
            : 'text-teal-600 hover:bg-teal-50'
        }`}
      >
        <i className="ri-links-line"></i>
        <span className="hidden sm:inline">GMB</span>
        <i
          className={`ri-arrow-down-s-line text-xs transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        ></i>
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 top-full mt-2 w-72 rounded-xl border shadow-lg z-50 ${
            darkMode
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          }`}
        >
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-teal-600"></i>
              </div>
              <div>
                <p
                  className={`text-sm font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Connected
                </p>
                <p
                  className={`text-xs ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {businessName}
                </p>
              </div>
            </div>

            <div
              className={`text-xs mb-4 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              Last synced: {lastSynced}
            </div>

            {!showDisconnectConfirm ? (
              <div className="space-y-2">
                <button
                  onClick={() => {
                    onSync();
                    setIsOpen(false);
                  }}
                  disabled={isSyncing}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-60 ${
                    darkMode
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {isSyncing ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-refresh-line"></i>
                  )}
                  {isSyncing ? 'Syncing...' : 'Sync Now'}
                </button>

                <button
                  onClick={() => setShowDisconnectConfirm(true)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    darkMode
                      ? 'text-red-400 hover:bg-gray-700'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                >
                  <i className="ri-links-line"></i>
                  Disconnect GMB
                </button>
              </div>
            ) : (
              <div
                className={`p-3 rounded-lg border ${
                  darkMode
                    ? 'bg-red-900/20 border-red-800'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <p
                  className={`text-sm font-medium mb-1 ${
                    darkMode ? 'text-red-400' : 'text-red-700'
                  }`}
                >
                  Disconnect GMB?
                </p>
                <p
                  className={`text-xs mb-3 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  This will remove all GMB data from the platform.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDisconnectConfirm(false)}
                    className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      darkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onDisconnect();
                      setIsOpen(false);
                    }}
                    className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
